import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';

import { HiDocumentAdd } from 'react-icons/hi';

import { useQuery, useQueryClient } from 'react-query';
import { fetchEquipmentInventory } from 'api/equipment';

import { washHub } from 'store/slices';

import { DataTable } from 'components/modules/DataTable';
import { Filters } from 'components/primitives/Filters';
import { StatCards } from 'components/primitives/StatCards';
import {
  AddEquipmentModal,
  AddEquipmentModalRef,
} from 'components/modules/AddEquipmentModal';

import { buildEquipmentInventoryColumns } from './equipmentInventoryColumns';

const PAGE_SIZE = 10;

/**
 * The equipment units the selected wash hub owns, one row per unit, and the
 * way to add more (ClickUp 86bc4et79). Like the Items and Cases tabs, the
 * status cards and Filters (SKU or site) narrow the list. Equipment SKUs
 * themselves are managed on the SKU page.
 */
export const EquipmentInventoryTable: FC = () => {
  const washHubId = washHub.selectors.useSelectedWashHubId();

  const addEquipmentModalRef = useRef<AddEquipmentModalRef>(null);

  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<number[]>();
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  // Which Filters section is in use; Filters shows that section's selection.
  const [filterType, setFilterType] = useState<string>();

  const clearFilters = () => {
    setPage(1);
    setStatus(undefined);
    setSku(undefined);
    setVenue(undefined);
    setFilterType(undefined);
  };

  useEffect(clearFilters, [washHubId]);

  const { data, isLoading, isFetching } = useQuery(
    [`fetch-equipment-inventory-${washHubId}`, page, status, sku, venue],
    () =>
      fetchEquipmentInventory({
        washHub: washHubId,
        page,
        limit: PAGE_SIZE,
        ...(status?.length ? { status } : {}),
        ...(sku?.length ? { sku } : {}),
        ...(venue?.length ? { venue } : {}),
      }),
    { enabled: Boolean(washHubId), keepPreviousData: true },
  );

  const onApplyFilters = (selected: string[], type: string) => {
    setPage(1);
    setFilterType(type);
    if (type === 'sku') setSku(selected);
    if (type === 'site') setVenue(selected);
  };

  const onClearFilters = () => {
    setPage(1);
    setFilterType(undefined);
    setSku(undefined);
    setVenue(undefined);
  };

  const onUpdateClick = () => {
    clearFilters();
    queryClient.invalidateQueries(`fetch-equipment-inventory-${washHubId}`);
    queryClient.invalidateQueries(`fetch-equipment-status-count-${washHubId}`);
  };

  const pagination = {
    page,
    setPage,
    totalCount: data?.total || 0,
    totalPages: data?.totalPages || 0,
  };

  const columns = useMemo(() => buildEquipmentInventoryColumns(), []);

  return (
    <>
      <Flex alignItems="baseline" justify="space-between">
        <Box />
        <Button
          w="200px"
          leftIcon={<HiDocumentAdd size="20px" />}
          isDisabled={!washHubId}
          onClick={() => {
            addEquipmentModalRef?.current?.onOpen?.();
          }}>
          Add Equipment
        </Button>
      </Flex>
      <Flex h="20px" />
      <StatCards
        type="equipment"
        selected={status}
        onSelectStatus={(selected) => {
          if (Array.isArray(selected)) {
            setPage(1);
            setStatus(selected);
          }
        }}
      />
      <Flex>
        <Filters
          type="equipment"
          venue={filterType ? (filterType === 'sku' ? sku : venue) : []}
          isFullButton
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
        />
        <Flex h="10px" w="20px" />
        <Button
          w="180px"
          borderRadius="5px"
          onClick={onUpdateClick}
          isLoading={isFetching}>
          Update Table
        </Button>
      </Flex>
      <DataTable
        minW="420px"
        data={washHubId ? data?.items || [] : []}
        columns={columns}
        pagination={washHubId ? pagination : undefined}
        isLoading={Boolean(washHubId) && (isLoading || isFetching)}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">
              {washHubId
                ? 'No equipment at this wash hub'
                : 'Select a wash hub to see its equipment'}
            </Text>
          </Center>
        )}
      />
      <AddEquipmentModal ref={addEquipmentModalRef} />
    </>
  );
};
